import React, { PureComponent, Fragment, MouseEvent } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import {
  CloudDownload as DownloadIcon,
  Create as PencilIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { Menu, MenuItem } from '@mui/material';
import ShareDialog from '../../../../Search/Results/ShareDialog/component';
import ShareEmailDialog from '../../../../Search/Results/ShareEmailDialog/component';
import DeleteAssetsDialog from '../../../../Search/DeleteAssetsDialog/component';
import UpdateAssetGameScheduleDialog from '../../../UpdateAssetGameScheduleDialog/component';
import { IAppState } from '../../../../../state/reducers';
import { queryGames } from '../../../../../state/exchange/searchResults';
import { toggleShareDialog } from '../../../../../state/shareDialog';
import { toggleEmailDialog } from '../../../../../state/shareEmailDialog';
import { toggleDeleteAssets } from '../../../../../state/deleteAssets';
import { toggleAssetEditable, restoreActiveAsset, updateAsset } from '../../../../../state/exchange/activeAsset';
import { toggleUpdateAssetGameScheduleDialog } from '../../../../../state/exchange/updateAssetGameScheduleDialog';
import {
  selectIsUserVaultSuperAdmin,
  selectIsUserExchangeAdmin,
  selectUserOrgsSecurityProfilesIds,
  selectIsUserExchangeVendor,
  selectHasDownloadPermission,
} from '../../../../../state/user/userOrgRole';
import { downloadAsset } from '../../../../../utils/downloadAsset';
import {
  OperationsContainer, Button, AnchoredButton, AnchoredGroup,
} from './styles';
import {
  IState,
  IProps,
  IStateProps,
  IDispatchProps,
} from './interfaces';
import { copyAssetsToClipboard } from '../../../../../utils/copyAssetsToClipbaord';
import { Sport } from '../../../../../enums';

class OperationsArea extends PureComponent<IProps, IState> {
  state: Readonly<IState> = {
    shareAnchorEl: null,
    shareOpen: false,
  };

  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  toggleEdit = () => {
    const { isEditable, toggleEditable, toggleRestore } = this.props;
    toggleEditable();
    if (isEditable) {
      toggleRestore();
    }
    ReactTooltip.hide();
  };

  saveEdit = async () => {
    this.props.updateAsset();
  };

  openShareMenu = (e: MouseEvent) => {
    this.setState({
      shareAnchorEl: e.target as HTMLElement,
      shareOpen: true,
    });
  };

  closeShareMenu = () => {
    this.setState({
      shareOpen: false,
    });
  };

  openShareEmailDialog = (selectedAsset: any) => {
    const { toggleEmailDialogConnect } = this.props;

    toggleEmailDialogConnect([selectedAsset]);
    this.closeShareMenu();
  };

  openShareDialog = (selectedAsset: any) => {
    const { toggleShareDialogConnect } = this.props;

    toggleShareDialogConnect([selectedAsset]);
    this.closeShareMenu();
  };

  openGameScheduleDialog = () => {
    const {
      toggleUpdateAssetGameScheduleDialog,
      rawAssetDetails: {
        exchangeScheduleId,
      },
    } = this.props;

    toggleUpdateAssetGameScheduleDialog(exchangeScheduleId);
  };

  render() {
    const {
      rawAssetDetails,
      isEditable,
      isVaultSuperAdmin,
      isExchangeAdmin,
      canDownload,
      userOrgsSecurityProfiles,
      toggleDelete,
    } = this.props;

    const {
      shareAnchorEl,
      shareOpen,
    } = this.state;

    // note: delete and share components expects AssetHit
    const selectedAsset = {
      ...rawAssetDetails,
      _source: {
        title: rawAssetDetails.title,
        videoType: rawAssetDetails.videoType,
      },
    };

    const {
      _id: assetId,
      title: assetName,
    } = rawAssetDetails;

    const isOwner = userOrgsSecurityProfiles.includes(rawAssetDetails.owner);
    const hasAccess = rawAssetDetails.securityProfiles.some((sp: string) => userOrgsSecurityProfiles.includes(sp));
    return (
      <OperationsContainer data-tip="Asset Operations">
        <DeleteAssetsDialog />
        {(isVaultSuperAdmin || isExchangeAdmin || isOwner || hasAccess) && !(!canDownload && selectedAsset.sourceTeamId.Sport === Sport.Football) && (
          <Button
            variant="primaryOnWhite"
            onClick={() => downloadAsset(rawAssetDetails, isVaultSuperAdmin)}
            data-tip="Download"
            data-for="operations-exchange-tooltip"
            id="Operations.Exchange.Download"
          >
            <DownloadIcon />
          </Button>
        )}
        {(isVaultSuperAdmin || isOwner) && (
          <>
            <ShareDialog />
            <ShareEmailDialog />
            <UpdateAssetGameScheduleDialog
              assetId={assetId}
              assetName={assetName}
            />
            <Menu
              data-tip="Share Menu"
              open={shareOpen}
              anchorEl={shareAnchorEl}
              onClose={() => this.closeShareMenu()}
            >
              <MenuItem
                data-tip="Share Vault"
                onClick={() => this.openShareDialog(selectedAsset)}
              >
                Share to Vault
              </MenuItem>
              <MenuItem
                data-tip="Share Email"
                onClick={() => this.openShareEmailDialog(selectedAsset)}
              >
                Send Email Link
              </MenuItem>
              <MenuItem
                onClick={() => {
                  copyAssetsToClipboard([selectedAsset]);
                  this.closeShareMenu();
                }}
              >
                Copy Link to Clipboard
              </MenuItem>
            </Menu>
            <Button
              variant="primaryOnWhite"
              onClick={this.openGameScheduleDialog}
              data-tip="Update by Game Schedule"
              data-for="operations-exchange-tooltip"
              id="Operations.Exchange.GameSchedule"
            >
              <CalendarIcon />
            </Button>
            <Button
              variant="primaryOnWhite"
              onClick={(e) => this.openShareMenu(e)}
              data-tip="Share"
              data-for="operations-exchange-tooltip"
              id="Operations.Exchange.Share"
            >
              <ShareIcon />
            </Button>
            <AnchoredGroup>
              <AnchoredButton
                variant="primaryOnWhite"
                onClick={() => toggleDelete(true, [selectedAsset])}
                disabled={!isVaultSuperAdmin && !isOwner}
                data-tip="Delete"
                data-for="operations-exchange-tooltip"
                id="Operations.Exchange.Delete"
              >
                <DeleteIcon />
              </AnchoredButton>
              {!isEditable && (
                <AnchoredButton
                  variant="primaryOnWhite"
                  onClick={() => this.toggleEdit()}
                  data-tip="Edit"
                  data-for="operations-exchange-tooltip"
                  id="Operations.Exchange.Edit"
                >
                  <PencilIcon />
                </AnchoredButton>
              )}
              {isEditable && (
                <>
                  <AnchoredButton
                    variant="primaryOnWhite"
                    onClick={() => this.toggleEdit()}
                  >
                    Cancel
                  </AnchoredButton>
                  <AnchoredButton
                    variant="primaryOnWhite"
                    onClick={() => this.saveEdit()}
                  >
                    Save
                  </AnchoredButton>
                </>
              )}
            </AnchoredGroup>
          </>
        )}
        <ReactTooltip
          id="operations-exchange-tooltip"
          delayShow={250}
          effect="solid"
        />
      </OperationsContainer>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  rawAssetDetails: state.exchange.activeAsset.rawAssetDetails,
  editableAssetDetails: state.exchange.activeAsset.editableAssetDetails,
  isEditable: state.exchange.activeAsset.isEditable,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
  isExchangeAdmin: selectIsUserExchangeAdmin(state),
  userOrgsSecurityProfiles: selectUserOrgsSecurityProfilesIds(state),
  canDownload: selectHasDownloadPermission(state),
});

const mapDispatchToProps: IDispatchProps = {
  toggleDelete: toggleDeleteAssets,
  toggleEditable: toggleAssetEditable,
  toggleRestore: restoreActiveAsset,
  queryGamesConnect: queryGames,
  toggleShareDialogConnect: toggleShareDialog,
  toggleEmailDialogConnect: toggleEmailDialog,
  toggleUpdateAssetGameScheduleDialog,
  updateAsset,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OperationsArea);
